import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Download, ExternalLink, FileText, Check } from "lucide-react";

const PDF_PATH = "/furniture/hospitality-hub-furniture-catalogue-2026.pdf";
const COVER_PATH = "/furniture/catalogue-cover-01.jpg";
const HERO_PATH = "/furniture/furniture-hero.jpg";
const CATALOGUE_TITLE = "HOSPITALITY HUB: FURNITURE CATALOGUE 2026";
const CATALOGUE_PAGES = 77;
const CATALOGUE_DOWNLOAD_NAME = "HOSPITALITY HUB - FURNITURE CATALOGUE 2026.pdf";

const FurniturePage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Furniture | Hospitality Hub";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <main data-testid="furniture-page">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[52vh] min-h-[420px] md:h-[600px] w-full overflow-hidden bg-[#1A1A1A]">
          <img
            src={HERO_PATH}
            alt="Premium hotel guestroom interior"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/85 via-[#1A1A1A]/45 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pb-14 md:pb-20">
              <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-4">
                <Link to="/" className="hover:text-[#D4AF37]" data-testid="furniture-breadcrumb-home">
                  Home
                </Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#D4AF37]">Furniture</span>
              </nav>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#D4AF37] mb-3">
                Hospitality Furniture Division
              </p>
              <h1
                data-testid="furniture-title"
                className="font-serif-display text-5xl lg:text-7xl text-white max-w-4xl leading-[1.05]"
              >
                Premium hotel furniture &amp; custom hospitality interiors.
              </h1>
              <p className="text-white/85 mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
                Complete guestroom, suite and public-area furniture packages built for hotels,
                developers, procurement teams, interior designers and renovation projects, at factory-direct pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CATALOGUE SECTION */}
      <section id="catalogue" data-testid="furniture-catalogue-section" className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Furniture Catalogue</p>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight">
              The complete Hospitality Hub furniture catalogue.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-2xl">
              Explore our complete hospitality furniture catalogue, featuring thoughtfully designed and
              custom-made solutions for hotel guestrooms, suites, public areas and hospitality spaces.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
            {/* Cover preview */}
            <div className="lg:col-span-6">
              <div className="relative group">
                <div className="absolute -inset-4 bg-[#D4AF37]/10 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity" />
                <a
                  href={PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="catalogue-cover-link"
                  className="relative block bg-white shadow-[0_30px_80px_rgb(0,0,0,0.5)] hover:shadow-[0_40px_100px_rgb(0,0,0,0.7)] transition-all duration-500 border border-white/10"
                >
                  <img
                    src={COVER_PATH}
                    alt={CATALOGUE_TITLE}
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-[#D4AF37] text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">
                    Official Catalogue
                  </div>
                </a>
              </div>
            </div>

            {/* Details + CTAs */}
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
                <FileText className="w-4 h-4" />
                PDF · {CATALOGUE_PAGES} pages · 2026 Edition
              </div>
              <h3
                data-testid="catalogue-title"
                className="font-serif-display text-3xl md:text-4xl leading-tight text-white"
              >
                {CATALOGUE_TITLE}
              </h3>
              <p className="mt-5 text-white/70 leading-relaxed">
                The authoritative reference for our full hospitality furniture portfolio, including brand-approved
                collections, custom-made solutions, dimensions and specifications for headboards,
                nightstands, bed bases, desks, dressers, TV panels, vanities, wardrobes, luggage benches,
                sofas, sleeper sofas, lounge and dining chairs, coffee &amp; side tables, ottomans,
                credenzas, communal tables, barstools and more.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={PDF_PATH}
                  download={CATALOGUE_DOWNLOAD_NAME}
                  data-testid="catalogue-download-btn"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
                >
                  <Download className="w-4 h-4" /> Download Catalogue
                </a>
                <a
                  href={PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="catalogue-view-btn"
                  className="inline-flex items-center gap-2 border border-white/50 hover:bg-white hover:text-[#1A1A1A] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> View Catalogue
                </a>
              </div>

              <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-xs text-white/60 uppercase tracking-[0.16em] border-t border-white/10 pt-6">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Brand-Approved</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Custom-Made</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Full Specifications</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Factory-Direct</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FurniturePage;
